import { TYPES_EVENEMENT } from '@artosera/shared';
import { TexteBilingueSchema } from './textes.js';
import { z } from 'zod';
import { ActualiteSchema } from './actualites.js';
import { ArtisteSchema } from './artistes.js';
import { CharteSchema } from './charte.js';
import { ExpositionSchema } from './expositions.js';
import { GalerieSchema } from './galerie.js';
import { MenusSchema } from './menus.js';
import { OeuvreSchema } from './oeuvres.js';
import { PagesSchema } from './pages.js';
import { RedirectionSchema, VersionSchema } from './redirections.js';
/** Options fonctionnelles du site, réglées dans la régie. */
export const OptionsSiteSchema = z.object({
    priseRendezVous: z
        .strictObject({ lien: z.literal('/rendez-vous/'), oeuvres: z.boolean() })
        .nullable()
        .optional(),
    /** Page « Œuvres disponibles » (section 3.4 : « si la galerie l'active »). */
    oeuvresDisponibles: z.boolean(),
    /** Formulaire de contact vers `/api/contact`. */
    formulaireContact: z.boolean(),
    /** Inscription à la lettre d'information vers `/api/newsletter`. */
    newsletter: z.boolean(),
    /**
     * `false` : le site ne doit pas être indexé (espace de test, `estTest`).
     * `robots.txt` ferme tout, aucun `sitemap.xml`, chaque page porte
     * `noindex, nofollow`, et le marqueur `MARQUEUR_NON_INDEXABLE` dit au
     * frontal de poser `X-Robots-Tag`. Absent : indexable.
     */
    indexable: z.boolean().optional(),
});
/**
 * Contrat de données du site (section 7.3). Le moteur ne connaît que cet
 * objet : ni base, ni réseau, ni disque. Ce qu'il reçoit est déjà filtré par
 * la régie — mais il refiltre les œuvres invisibles par sécurité.
 */
export const SiteDataSchema = z
    .object({
    version: VersionSchema,
    galerie: GalerieSchema,
    charte: CharteSchema,
    options: OptionsSiteSchema,
    menus: MenusSchema,
    artistes: z.array(ArtisteSchema),
    oeuvres: z.array(OeuvreSchema),
    expositions: z.array(ExpositionSchema),
    actualites: z.array(ActualiteSchema),
    evenements: z
        .array(z.object({
        titre: TexteBilingueSchema,
        type: z.enum(TYPES_EVENEMENT),
        debut: z.string().datetime(),
        fin: z.string().datetime().nullable(),
        journee: z.boolean(),
        lieu: z.string(),
        lien: z.string().url(),
    }))
        .optional(),
    pages: PagesSchema,
    redirections: z.array(RedirectionSchema),
})
    .superRefine((data, ctx) => {
    const doublons = (slugs) => {
        const vus = new Set();
        const repetes = new Set();
        for (const slug of slugs) {
            if (vus.has(slug))
                repetes.add(slug);
            vus.add(slug);
        }
        return [...repetes];
    };
    const collections = [
        ['artistes', data.artistes.map((a) => a.slug)],
        ['oeuvres', data.oeuvres.map((o) => o.slug)],
        ['expositions', data.expositions.map((e) => e.slug)],
        ['actualites', data.actualites.map((a) => a.slug)],
    ];
    for (const [nom, slugs] of collections) {
        const repetes = doublons(slugs);
        if (repetes.length > 0) {
            ctx.addIssue({
                code: 'custom',
                path: [nom],
                message: `slugs en double : ${repetes.join(', ')}`,
            });
        }
    }
    const artistes = new Set(data.artistes.map((a) => a.slug));
    const oeuvres = new Set(data.oeuvres.map((o) => o.slug));
    const expositions = new Set(data.expositions.map((e) => e.slug));
    const verifie = (connus, references, chemin, quoi) => {
        for (const reference of references) {
            if (!connus.has(reference)) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...chemin],
                    message: `${quoi} inconnu : ${reference}`,
                });
            }
        }
    };
    data.oeuvres.forEach((oeuvre, i) => {
        verifie(artistes, [oeuvre.artiste], ['oeuvres', i, 'artiste'], 'artiste');
    });
    data.artistes.forEach((artiste, i) => {
        verifie(oeuvres, artiste.oeuvres, ['artistes', i, 'oeuvres'], 'œuvre');
        verifie(expositions, artiste.expositions, ['artistes', i, 'expositions'], 'exposition');
    });
    data.expositions.forEach((exposition, i) => {
        verifie(artistes, exposition.artistes, ['expositions', i, 'artistes'], 'artiste');
        verifie(oeuvres, exposition.oeuvres, ['expositions', i, 'oeuvres'], 'œuvre');
    });
    data.actualites.forEach((actualite, i) => {
        verifie(artistes, actualite.artistes, ['actualites', i, 'artistes'], 'artiste');
        verifie(expositions, actualite.expositions, ['actualites', i, 'expositions'], 'exposition');
    });
    const cibles = new Set(data.redirections.map((r) => r.de));
    if (cibles.size !== data.redirections.length) {
        ctx.addIssue({
            code: 'custom',
            path: ['redirections'],
            message: 'deux redirections partent du même chemin',
        });
    }
});
/** Valide un contrat de données, ou lève avec le détail des écarts. */
export function validerSiteData(valeur) {
    return SiteDataSchema.parse(valeur);
}
/** Valide sans lever : utile pour afficher les écarts dans la régie. */
export function verifierSiteData(valeur) {
    return SiteDataSchema.safeParse(valeur);
}
//# sourceMappingURL=site-data.js.map